import React, { MutableRefObject, useContext, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  DemClassicParameters,
  GenerateDataClassicCustomOptions,
  GenerateDataClassicOptions,
  GenerateDataColmapOptions,
  GenerateDataOdmOptions,
  GenerateDataPmCustomOptions,
  GenerateDataPmOptions,
  OdmOptionsCustom,
  OptimizeClassicCustomOptions,
  OptimizeClassicOptions,
  OptimizeOdmOptions,
  OptimizePmOptions,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { WebApi } from '../../../../services/web-api';
import { FeatureFlagsManager } from '../../../../utils/core/feature-flags-manager';
import { WebsocketManager } from '../../../../utils/websocket/websocket-manager';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { useNonce } from '../../../hooks/use-nonce';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { FileSelectionButton } from '../../file-selection-button/file-selection-button';
import { Hyperlink } from '../../hyperlink/hyperlink';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import {
  ConvertInterfaceToJsonTemplateCompatibleType,
  extractJson,
  JsonEditor,
  JsonEntry,
  JsonTemplate,
} from '../../json-editor/json-editor';
import { List } from '../../list/list';
import { RadioButtons } from '../../radio-buttons/radio-buttons';
import { Stack } from '../../stack/stack';
import { ImageThresholdExceededInfoBox } from '../geodata-setup/pages/image-threshold-exceeded-info-box';
import { GeodataContext } from '../geodata-state';
import { geodataStatuses, UseGeodataModel } from '../use-geodata';
import { AlignerContext } from './aligner-state';
import { useGeodataCreateJobMutation } from './use-geodata-create-job-mutation';

type CustomPipelineType = 'odm' | 'colmap' | 'classic_generate_data' | 'none' | 'pm';

type OdmCustomType = ConvertInterfaceToJsonTemplateCompatibleType<
  OdmOptionsCustom & { settings: NonNullable<OdmOptionsCustom['settings']> }
>;
type GenerateDataColmapType =
  ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataColmapOptions>;
type GenerateDataOdmType = ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataOdmOptions>;
type OptimizeOdmType = ConvertInterfaceToJsonTemplateCompatibleType<OptimizeOdmOptions>;
type OptimizeClassicCustomType =
  ConvertInterfaceToJsonTemplateCompatibleType<OptimizeClassicCustomOptions>;
type GenerateDataClassicCustomType =
  ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataClassicCustomOptions>;
type GenerateDataPmCustomType =
  ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataPmCustomOptions>;
type GenerateDataClassicType =
  ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataClassicOptions>;
type OptimizeClassicType = ConvertInterfaceToJsonTemplateCompatibleType<OptimizeClassicOptions>;
type OptimizePmType = ConvertInterfaceToJsonTemplateCompatibleType<OptimizePmOptions>;
type GenerateDataPmType = ConvertInterfaceToJsonTemplateCompatibleType<GenerateDataPmOptions>;

export const OptimizeDialog = (props: {
  geodata: UseGeodataModel;
  onStartProcess: () => void;
  readyToQuickOptimize: boolean;
  dialog: ReturnType<typeof useDialog>;
}) => {
  const { t } = useTranslation();
  const { showOrganizationAdminModules, readyToOptimize } = useContext(AlignerContext);
  const [options, setOptions] = useState<'classic' | 'custom' | 'pm'>('classic');
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const jobConfiguration = useOptimizeConfigurationRef(props.geodata, options);

  const { createJob, errorList, isPending } = useGeodataCreateJobMutation(() => {
    props.dialog.hide();
    props.onStartProcess();
    publish(SubscriptionTopic.ToastrMessage, {
      type: 'info',
      message: t('gcpPage.processDialog.processStartedMessage', { ns: 'cloudProcessing' }),
    });
  });

  const infoText = useMemo(
    () =>
      readyToOptimize
        ? t('gcpPage.processDialog.optimizeInfo', { ns: 'cloudProcessing' })
        : t('gcpPage.processDialog.notReadyToOptimize', { ns: 'cloudProcessing' }),
    [readyToOptimize, t],
  );

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      maxHeight="expand"
      onClose={props.dialog.hide}
    >
      {{
        header: 'Optimera',
        content: (
          <Stack spacing={1}>
            {!isDisconnecting && (
              <>
                <InfoBox color="yellow">{infoText}</InfoBox>
                {showOrganizationAdminModules && (
                  <RadioButtons
                    items={[
                      {
                        value: 'classic',
                        name: 'Classic',
                      },
                      {
                        value: 'custom',
                        name: 'Custom',
                      },
                      ...(FeatureFlagsManager.instance.isEnabled('SK-6920-pm')
                        ? [{ value: 'pm' as const, name: 'Custom (PM) WIP Do not use' }]
                        : []),
                    ]}
                    onChange={(x) => setOptions(x)}
                  />
                )}
                <JsonEditor
                  plainStyling={options === 'classic'}
                  template={jobConfiguration.current}
                />
              </>
            )}
            {isDisconnecting && (
              <InfoBox color="yellow">
                <Stack direction="row" spacing={1}>
                  <Icon icon={['fad', 'spinner']} spin={true} />
                  <span>
                    {t('gcpPage.processDialog.disconnectingOptimizeInstance', {
                      ns: 'cloudProcessing',
                    })}
                  </span>
                </Stack>
              </InfoBox>
            )}
            {errorList}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={!readyToOptimize}
                loading={isPending || isDisconnecting}
                variant="contained"
                onClick={() => {
                  const body = extractJson(jobConfiguration.current);
                  if (props.readyToQuickOptimize && body.type === 'optimize_classic') {
                    WebsocketManager.instance.sendToUserId(
                      {
                        message: 'OptimizeInstanceTriggerOptimize',
                        geodataId: props.geodata.id,
                        resetAlignment: body.resetAlignment ?? false,
                      },
                      props.geodata.uploadedBy.id,
                    );
                    props.onStartProcess();
                    props.dialog.hide();
                  } else {
                    if (props.readyToQuickOptimize) {
                      setIsDisconnecting(true);
                      WebsocketManager.instance.sendToUserId(
                        {
                          message: 'OptimizeInstanceRequestDisconnect',
                          geodataId: props.geodata.id,
                        },
                        props.geodata.uploadedBy.id,
                      );
                      setTimeout(() => {
                        setIsDisconnecting(false);
                        createJob({
                          path: { geodataId: props.geodata.id },
                          body,
                        });
                      }, 5000);
                    } else {
                      createJob({
                        path: { geodataId: props.geodata.id },
                        body,
                      });
                    }
                  }
                }}
              >
                {t('optimize', { ns: 'cloudProcessing' })}
              </Button>
              <Button
                disabled={isPending || isDisconnecting}
                variant="text"
                onClick={props.dialog.hide}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const getDefaultDemSettings = (options: {
  hideCustomSettings: boolean;
  excludedByDefault: boolean;
}): JsonEntry<ConvertInterfaceToJsonTemplateCompatibleType<DemClassicParameters>> => ({
  title: options.hideCustomSettings
    ? 'DEM (Tillägg)'
    : 'DEM (Used for orthomosaic if surface data is "dem")',
  optional: true,
  excluded: options.excludedByDefault,
  currentValue: {
    sourceData: {
      title: 'Source data',
      currentValue: 'pointCloud',
      hidden: options.hideCustomSettings,
      optional: false,
      values: ['pointCloud', 'tiePoints', 'depthMaps'],
    },
    interpolation: {
      title: 'Interpolering',
      currentValue: 'enabled',
      optional: false,
      values: ['disabled', 'enabled', 'extrapolated'],
    },
    pointClasses: {
      title: 'Klasser',
      currentValue: 'all',
      optional: false,
      values: ['all', 'ground'],
    },
    resolution: {
      title: 'Upplösning (GSD)',
      currentValue: 0.02,
      optional: false,
    },
    tifPalette: {
      title: 'Färgsättning',
      currentValue: 'none',
      optional: false,
      values: ['gradient', 'none'],
    },
    hillshadeEnable: {
      title: 'Hillshading',
      currentValue: false,
      optional: false,
    },
    exportTif: {
      title: 'Exportera TIF (Tillägg)',
      currentValue: options.hideCustomSettings,
      hidden: options.hideCustomSettings,
      optional: false,
    },
    submitSkyViewPipelineJob: {
      title: 'Exportera till SkyView',
      hidden: options.hideCustomSettings,
      currentValue: false,
      optional: false,
    },
  },
});

export const StartProcessDialog = (props: {
  geodata: UseGeodataModel;
  readyToQuickOptimize: boolean;
  showImageThresholdExceededInfo: boolean;
  showUnoptimizedGcpWarning: boolean;
  showHighGcpErrorWarning: boolean;
  showLowGcpCountWarning: boolean;
  onStartProcess: () => void;
  dialog: ReturnType<typeof useDialog>;
}) => {
  const { t } = useTranslation();
  const { showOrganizationAdminModules } = useContext(AlignerContext);
  const [selectedType, setSelectedType] = useState<CustomPipelineType>('none');
  const [isLoadingPreviousValues, setIsLoadingPreviousValues] = useState(false);
  const [refreshKey, updateRefreshKey] = useNonce();
  const jobConfiguration = useGenerateDataConfigurationRef(selectedType, props.geodata);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showWarning, setShowWarning] = useState(
    props.showHighGcpErrorWarning ||
      props.showLowGcpCountWarning ||
      props.showUnoptimizedGcpWarning,
  );

  const { signer } = useContext(GeodataContext);
  const { createJob, errorList, isPending } = useGeodataCreateJobMutation(() => {
    props.dialog.hide();
    props.onStartProcess();
    publish(SubscriptionTopic.ToastrMessage, {
      type: 'info',
      message: t('gcpPage.processDialog.processStartedMessage', { ns: 'cloudProcessing' }),
    });
  });

  // Helper function to update nested values in the JSON template
  const updateNestedValues = (
    templateSection: JsonEntry<Record<string, any>>,
    settingsSection: Record<string, unknown>,
  ): void => {
    if (!templateSection?.currentValue || !settingsSection) {
      return;
    }

    templateSection.excluded = false;

    // Iterate through each key in the settings section
    Object.keys(settingsSection).forEach((key) => {
      const settingValue = settingsSection[key];
      const templateValue = (templateSection.currentValue as Record<string, any>)[key];

      if (templateValue && typeof templateValue === 'object' && 'currentValue' in templateValue) {
        // This is a leaf node in the JSON template, update its currentValue
        const leafNode = templateValue as JsonEntry<any>;
        leafNode.excluded = false;
        leafNode.currentValue = settingValue;
      } else if (
        templateValue &&
        typeof templateValue === 'object' &&
        !('currentValue' in templateValue)
      ) {
        // This is a nested object, recurse deeper
        updateNestedValues(
          templateValue as JsonEntry<Record<string, any>>,
          settingValue as Record<string, unknown>,
        );
      }
    });
  };

  // Helper function to apply settings to the configuration
  const applySettingsToConfiguration = (settings: GenerateDataColmapOptions): void => {
    const colmapConfig = jobConfiguration.current as JsonTemplate<GenerateDataColmapType>;

    // Update each section of the COLMAP configuration
    const sectionsToUpdate: (keyof GenerateDataColmapOptions)[] = [
      'featureExtraction',
      'spatialMatcher',
      'sequentialMatcher',
      'exhaustiveMatcher',
      'siftMatching',
      'twoViewGeometry',
      'mapper',
      'modelAlignment',
      'imageUndistortion',
      'patchMatchStereo',
      'stereoFusion',
      'pointCloud',
      'pointCloudAlignment',
    ];

    sectionsToUpdate.forEach((sectionKey) => {
      const settingsSection = settings[sectionKey];
      const templateSection = colmapConfig[sectionKey as keyof typeof colmapConfig];

      if (settingsSection && templateSection) {
        updateNestedValues(
          templateSection as JsonEntry<Record<string, any>>,
          settingsSection as Record<string, unknown>,
        );
      }
    });

    // Force re-render by updating the refresh key
    updateRefreshKey();
  };

  const loadPreviousValues = async (): Promise<void> => {
    if (!signer || selectedType !== 'colmap') {
      return;
    }

    setIsLoadingPreviousValues(true);
    try {
      const signedUrl = signer.getSignedRequest('colmap/settings.json');
      const settingsText = await WebApi.fetchS3DataAsText(signedUrl);
      const settings: GenerateDataColmapOptions = JSON.parse(settingsText);

      applySettingsToConfiguration(settings);

      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: 'Previous COLMAP settings loaded successfully',
      });
    } catch (error) {
      console.error('Failed to load previous values:', error);
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: 'Failed to load previous values. Please check if the settings file exists.',
      });
    } finally {
      setIsLoadingPreviousValues(false);
    }
  };

  const handleFileUpload = async (files: File[]): Promise<void> => {
    if (selectedType !== 'colmap' || files.length === 0) {
      return;
    }

    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.json')) {
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message: 'Please select a valid JSON file.',
      });
      return;
    }

    setIsLoadingPreviousValues(true);
    try {
      const fileContent = await file.text();
      const settings: GenerateDataColmapOptions = JSON.parse(fileContent);

      applySettingsToConfiguration(settings);

      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: 'COLMAP settings loaded from file successfully',
      });
    } catch (error) {
      console.error('Failed to load settings from file:', error);
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'error',
        message:
          'Failed to load settings from file. Please check if the file contains valid COLMAP settings JSON.',
      });
    } finally {
      setIsLoadingPreviousValues(false);
    }
  };

  const warnings = useMemo(() => {
    const items: NonNullable<React.ReactNode>[] = [];
    if (props.showHighGcpErrorWarning) {
      items.push(
        <Trans
          components={{ br: <br /> }}
          i18nKey="gcpPage.processDialog.highGcpErrorWarning"
          ns="cloudProcessing"
        />,
      );
    }
    if (props.showLowGcpCountWarning) {
      items.push(t('gcpPage.processDialog.lowGcpCountWarning', { ns: 'cloudProcessing' }));
    }
    if (props.showUnoptimizedGcpWarning) {
      items.push(t('gcpPage.processDialog.unoptimizedGcpWarning', { ns: 'cloudProcessing' }));
    }
    return items;
  }, [
    props.showHighGcpErrorWarning,
    props.showLowGcpCountWarning,
    props.showUnoptimizedGcpWarning,
    t,
  ]);

  const readyToProcess = useMemo(
    () =>
      showOrganizationAdminModules
        ? geodataStatuses.idleStatuses.includes(props.geodata.status)
        : geodataStatuses.readyToStartProcessStatuses.includes(props.geodata.status),
    [props.geodata.status, showOrganizationAdminModules],
  );

  const showCustomSettings = useMemo(
    () =>
      readyToProcess &&
      (showOrganizationAdminModules || FeatureFlagsManager.instance.isEnabled('SK-6726-dem')),
    [showOrganizationAdminModules, readyToProcess],
  );
  const infoText = useMemo(
    () =>
      readyToProcess ? (
        <Trans
          components={{ br: <br /> }}
          i18nKey="gcpPage.processDialog.startProcessInfo"
          ns="cloudProcessing"
        />
      ) : (
        t('gcpPage.processDialog.notReadyToProcess', { ns: 'cloudProcessing' })
      ),
    [readyToProcess, t],
  );

  const orgAdminRadioButtons = useMemo<{ value: CustomPipelineType; name: string }[]>(() => {
    const buttons: { value: CustomPipelineType; name: string }[] = [
      {
        value: 'none',
        name: 'Default',
      },
      {
        value: 'classic_generate_data',
        name: 'Custom (Classic)',
      },
      {
        value: 'odm',
        name: 'Custom (Beta)',
      },
      {
        value: 'colmap',
        name: 'Custom (Colmap)',
      },
    ];

    if (FeatureFlagsManager.instance.isEnabled('SK-6920-pm')) {
      buttons.push({
        value: 'pm',
        name: 'Custom (PM) WIP Do not use',
      });
    }

    return buttons;
  }, []);

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      maxHeight="expand"
      onClose={props.dialog.hide}
    >
      {{
        header: t('startProcess', { ns: 'cloudProcessing' }),
        content: (
          <Stack spacing={1}>
            {showWarning ? (
              <InfoBox color="yellow">
                <Stack spacing={1}>
                  <span>
                    <strong>
                      {t('gcpPage.processDialog.warningHeader', { ns: 'cloudProcessing' })}
                    </strong>
                  </span>
                  <List items={warnings} marker="disc" />
                  <Hyperlink url="#" onClick={() => setShowWarning(false)}>
                    {t('gcpPage.processDialog.understandButContinue', { ns: 'cloudProcessing' })}
                  </Hyperlink>
                </Stack>
              </InfoBox>
            ) : (
              <>
                {!isDisconnecting && (
                  <>
                    {props.showImageThresholdExceededInfo && <ImageThresholdExceededInfoBox />}
                    <InfoBox color="yellow">{infoText}</InfoBox>
                    {showOrganizationAdminModules && (
                      <RadioButtons
                        items={orgAdminRadioButtons}
                        onChange={(x) => setSelectedType(x)}
                      />
                    )}
                    {showCustomSettings && (
                      <JsonEditor
                        key={refreshKey}
                        plainStyling={selectedType === 'none'}
                        template={jobConfiguration.current}
                      />
                    )}
                  </>
                )}
                {isDisconnecting && (
                  <InfoBox color="yellow">
                    <Stack direction="row" spacing={1}>
                      <Icon icon={['fad', 'spinner']} spin={true} />
                      <span>
                        {t('gcpPage.processDialog.disconnectingOptimizeInstance', {
                          ns: 'cloudProcessing',
                        })}
                      </span>
                    </Stack>
                  </InfoBox>
                )}
                {errorList}
              </>
            )}
          </Stack>
        ),
        footer: {
          right: (
            <>
              {selectedType === 'colmap' && (
                <>
                  <FileSelectionButton
                    accept=".json"
                    color="secondary"
                    disabled={isLoadingPreviousValues}
                    leftIcon={{ icon: ['fad', 'file'] }}
                    text="Load from File"
                    variant="contained"
                    onFilesSelected={handleFileUpload}
                  />
                  <Button
                    color="secondary"
                    leftIcon={{ icon: ['fad', 'cloud-download'] }}
                    loading={isLoadingPreviousValues}
                    variant="contained"
                    onClick={loadPreviousValues}
                  >
                    Load Previous Values
                  </Button>
                </>
              )}
              {!showWarning && (
                <Button
                  color="primary"
                  disabled={!readyToProcess}
                  loading={isPending || isDisconnecting}
                  variant="contained"
                  onClick={() => {
                    if (props.readyToQuickOptimize) {
                      setIsDisconnecting(true);
                      WebsocketManager.instance.sendToUserId(
                        {
                          message: 'OptimizeInstanceRequestDisconnect',
                          geodataId: props.geodata.id,
                        },
                        props.geodata.uploadedBy.id,
                      );
                      setTimeout(() => {
                        setIsDisconnecting(false);
                        createJob({
                          path: { geodataId: props.geodata.id },
                          body: extractJson(jobConfiguration.current),
                        });
                      }, 5000);
                    } else {
                      createJob({
                        path: { geodataId: props.geodata.id },
                        body: extractJson(jobConfiguration.current),
                      });
                    }
                  }}
                >
                  {t('startProcess', { ns: 'cloudProcessing' })}
                </Button>
              )}
              <Button
                disabled={isPending || isDisconnecting}
                variant="text"
                onClick={props.dialog.hide}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

function getSourceDataDefaultTemplate(): JsonTemplate<
  NonNullable<GenerateDataClassicCustomType['sourceData']>
> {
  return {
    projectData: {
      title: 'Project Data',
      optional: false,
      currentValue: 'classicProjectZip',
      values: ['classicProjectZip', 'none'],
    },
    imageData: {
      title: 'Import image data',
      optional: true,
      excluded: true,
      currentValue: {
        type: {
          title: 'Source',
          currentValue: 'geodata',
          values: ['geodata', 'drone-harmony'],
          optional: false,
        },
        id: {
          title: 'ID (Geodata ID or DH Task ID)',
          currentValue: '',
          optional: false,
        },
      },
    },
    gcpData: {
      title: 'Import GCP data',
      optional: true,
      excluded: true,
      currentValue: {
        type: {
          title: 'Source',
          currentValue: 'geodata',
          values: ['geodata', 'gcp-collection'],
          optional: false,
        },
        id: {
          title: 'ID (Geodata ID or GCP Collection ID)',
          currentValue: '',
          optional: false,
        },
      },
    },
  };
}

function getAlignmentDefaultTemplate(): JsonTemplate<
  NonNullable<GenerateDataClassicCustomType['alignment']>
> {
  return {
    resetMatches: {
      title: 'Force reset matches',
      optional: false,
      currentValue: false,
    },
    resetAlignment: {
      title: 'Reset alignment',
      currentValue: false,
      optional: false,
    },
    matchingQuality: {
      title: 'Matching quality',
      optional: false,
      currentValue: 'highest',
      values: ['highest', 'high', 'medium', 'low', 'lowest'],
    },
    useImageGpsDuringOptimize: {
      title: 'Use GPS data during optimize step',
      optional: true,
      excluded: true,
      hidden: true,
      currentValue: false,
    },
    referencePreselectionMode: {
      title: 'Reference preselection mode',
      optional: false,
      currentValue: 'source',
      values: ['source', 'estimated', 'sequential'],
    },
    fitF: {
      title: 'fitF',
      optional: false,
      currentValue: false,
    },
    fitCx: {
      title: 'fitCx',
      optional: false,
      currentValue: true,
    },
    fitCy: {
      title: 'fitCy',
      optional: false,
      currentValue: true,
    },
    fitK1: {
      title: 'fitK1',
      optional: false,
      currentValue: true,
    },
    fitK2: {
      title: 'fitK2',
      optional: false,
      currentValue: true,
    },
    fitK3: {
      title: 'fitK3',
      optional: false,
      currentValue: true,
    },
    fitP1: {
      title: 'fitP1',
      optional: false,
      currentValue: true,
    },
    fitP2: {
      title: 'fitP2',
      optional: false,
      currentValue: true,
    },
    fitB1: {
      title: 'fitB1',
      optional: false,
      currentValue: true,
    },
    fitB2: {
      title: 'fitB2',
      optional: false,
      currentValue: true,
    },
    fitCorrections: {
      title: 'fitCorrections',
      optional: false,
      currentValue: false,
    },
    adaptiveFitting: {
      title: 'adaptiveFitting',
      optional: false,
      currentValue: true,
    },
    tiepointCovariance: {
      title: 'tiepointCovariance',
      optional: false,
      currentValue: false,
    },
  };
}

export const ClassicOptimizeConfiguation = (props: {
  onClick: (payload: OptimizeClassicCustomType) => void;
}) => {
  const template = useRef<JsonTemplate<OptimizeClassicCustomType>>({
    type: {
      title: 'Type',
      optional: false,
      currentValue: 'optimize_classic_custom',
      values: ['optimize_classic_custom'],
    },
    sourceData: {
      title: 'Source data',
      optional: true,
      currentValue: getSourceDataDefaultTemplate(),
    },
    alignment: {
      title: 'Alignment',
      optional: false,
      currentValue: getAlignmentDefaultTemplate(),
    },
  } satisfies JsonTemplate<OptimizeClassicCustomType>);

  return (
    <JsonEditor
      template={template.current}
      onConfirm={(payload: OptimizeClassicCustomType) => props.onClick(payload)}
    />
  );
};

const useOptimizeConfigurationRef = (
  geodata: GeodataModel,
  options: 'classic' | 'custom' | 'pm',
): MutableRefObject<
  JsonTemplate<OptimizeClassicType | OptimizeClassicCustomType | OptimizeOdmType | OptimizePmType>
> => {
  const isOdm = useMemo(() => geodata.processType === 'ODM', [geodata.processType]);
  const isPm = useMemo(() => geodata.processType === 'PM', [geodata.processType]);
  const { showOrganizationAdminModules } = useContext(AlignerContext);
  const { t } = useTranslation();

  const optimizeConfiguration = useRef<JsonTemplate<OptimizeClassicType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'optimize_classic',
      values: ['optimize_classic'],
    },
    resetAlignment: {
      title: t('resetImagePositions', { ns: 'cloudProcessing' }),
      optional: false,
      currentValue: false,
    },
    sendEmailNotification: {
      title: t('sendEmailNotification', { ns: 'cloudProcessing' }),
      optional: false,
      hidden: !showOrganizationAdminModules,
      currentValue: false,
    },
  } satisfies JsonTemplate<OptimizeClassicType>);

  const optimizeOdmConfiguration = useRef<JsonTemplate<OptimizeOdmType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'optimize_odm',
      values: ['optimize_odm'],
    },
  } satisfies JsonTemplate<OptimizeOdmType>);

  const optimizeCustomConfiguration = useRef<JsonTemplate<OptimizeClassicCustomType>>({
    type: {
      title: 'Type',
      optional: false,
      currentValue: 'optimize_classic_custom',
      values: ['optimize_classic_custom'],
    },
    sourceData: {
      title: 'Source data',
      optional: true,
      currentValue: getSourceDataDefaultTemplate(),
    },
    alignment: {
      title: 'Alignment',
      optional: false,
      currentValue: getAlignmentDefaultTemplate(),
    },
  } satisfies JsonTemplate<OptimizeClassicCustomType>);

  const optimizePmConfiguration = useRef<JsonTemplate<OptimizePmType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'optimize_pm',
      values: ['optimize_pm'],
    },
  } satisfies JsonTemplate<OptimizePmType>);

  if (isOdm) {
    return optimizeOdmConfiguration;
  }

  if (options === 'pm' || isPm) {
    return optimizePmConfiguration;
  }

  if (options === 'custom') {
    return optimizeCustomConfiguration;
  }

  return optimizeConfiguration;
};

const useGenerateDataConfigurationRef = (
  state: CustomPipelineType,
  geodata: GeodataModel,
): MutableRefObject<
  JsonTemplate<
    | GenerateDataClassicType
    | GenerateDataOdmType
    | GenerateDataClassicCustomType
    | OdmCustomType
    | GenerateDataColmapType
    | GenerateDataPmCustomType
    | GenerateDataPmType
  >
> => {
  const isOdm = useMemo(() => geodata.processType === 'ODM', [geodata.processType]);
  const isPm = useMemo(() => geodata.processType === 'PM', [geodata.processType]);

  const odmConfiguration = useRef<JsonTemplate<GenerateDataOdmType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'generate_data_odm',
      values: ['generate_data_odm'],
    },
  } satisfies JsonTemplate<GenerateDataOdmType>);

  const classicConfiguration = useRef<JsonTemplate<GenerateDataClassicType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'generate_data_classic',
      values: ['generate_data_classic'],
    },
    imageSource: {
      title: 'Image source',
      optional: true,
      hidden: true,
      currentValue: undefined,
      values: [undefined],
    },
    dem: getDefaultDemSettings({ hideCustomSettings: true, excludedByDefault: true }),
  } satisfies JsonTemplate<GenerateDataClassicType>);

  const pmConfiguration = useRef<JsonTemplate<GenerateDataPmType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'generate_data_pm',
      values: ['generate_data_pm'],
    },
  } satisfies JsonTemplate<GenerateDataPmType>);

  const colmapConfiguration = useRef<JsonTemplate<GenerateDataColmapType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'colmap_custom',
      values: ['colmap_custom'],
    },
    featureExtraction: {
      title: 'Feature Extraction',
      optional: false,
      currentValue: {
        edgeThreshold: {
          title: 'Edge Threshold',
          tooltip:
            "If your dataset has lots of repeated linear textures (e.g., fields, rooftops, runways) and you're seeing bad matches along edges, you can lower it (e.g., to 5–8) to reject edge-only features.",
          optional: false,
          currentValue: 10,
        },
        peakThreshold: {
          title: 'Peak Threshold',
          tooltip:
            'Lower to detect more features in texture-poor areas (e.g., 0.004–0.006). Raise to reduce noise on very textured obliques.',
          optional: false,
          currentValue: 0.0066666666666666671,
        },
        upright: {
          title: 'Upright',
          tooltip:
            'Consider turning on for mostly-nadir imagery (little in-plane rotation). Speeds things up and can improve match consistency.',
          optional: false,
          currentValue: false,
        },
        maxNumFeatures: {
          title: 'Max Number of Features',
          tooltip: 'Drone nadir scenes can be low-texture; 10k–12k can help if matches are sparse.',
          optional: false,
          currentValue: 10000,
        },
        maxImageSize: {
          title: 'Max Image Size',
          tooltip:
            'Cap image size to speed up extraction (e.g., 3200–4000 px long edge). Increase if you need more detail; decrease for speed/memory.',
          optional: false,
          currentValue: 4000,
        },
        singleCamera: {
          title: 'Single Camera',
          tooltip:
            'Keep on if all images come from the same rig/lens and focal length (typical for one drone flight). Keeps one intrinsics estimate and improves stability.',
          optional: false,
          currentValue: true,
        },
        domainSizePooling: {
          title: 'Domain Size Pooling',
          tooltip: (
            <>
              Domain Size Pooling (DSP) for SIFT: averages descriptor over multiple scales.
              <br />
              Improves match robustness on low-texture or scale-varying regions (e.g., dirt,
              fields),
              <br />
              at a moderate compute cost. Great when features are weak or repetitive.
            </>
          ),
          optional: false,
          currentValue: false,
        },
        estimateAffineShape: {
          title: 'Estimate Affine Shape',
          tooltip: (
            <>
              Estimate affine-invariant keypoints (tilt/shape) for SIFT.
              <br />
              Boosts repeatability under viewpoint changes (slanted surfaces, oblique angles).
              <br />
              Heavier computation; pair with DSP for tough scenes. Leave off for speed.
            </>
          ),
          optional: false,
          currentValue: false,
        },
        cameraModel: {
          title: 'Camera Model',
          tooltip: (
            <>
              Wide FOV / strong distortion / obliques: prefer OPENCV (fx,fy,cx,cy,k1,k2,p1,p2) or
              RADIAL (k1,k2).
              <br />
              <br />
              Standard drone nadir with modest lenses: SIMPLE_RADIAL is often fine and keeps BA
              stable/fast.
              <br />
              <br />
              SIMPLE_RADIAL: f, cx, cy, k₁ (1 radial term only, no tangential). Works OK for mild
              distortion.
              <br />
              RADIAL: f, cx, cy, k₁, k₂, k₃ (Models stronger radial distortion; still no
              tangential).
              <br />
              OPENCV: f, cx, cy, k₁, k₂, p₁, p₂ (Adds tangential distortion). Recommended for most
              drone lenses.
              <br />
              FULL_OPENCV: f, cx, cy, k₁, k₂, p₁, p₂, k₃, k₄, k₅, k₆ (Full Brown–Conrady model;
              overkill unless you have lots of images).
              <br />
              FOV: f, cx, cy, ω (One-parameter fisheye-style model; great for ultra-wide lenses).
            </>
          ),
          optional: false,
          currentValue: 'SIMPLE_RADIAL',
          values: [
            'SIMPLE_RADIAL',
            'RADIAL',
            'OPENCV',
            'OPENCV_FISHEYE',
            'FULL_OPENCV',
            'FOV',
            'SIMPLE_PINHOLE',
            'PINHOLE',
          ],
        },
      },
    },
    spatialMatcher: {
      title: 'Spatial Matcher',
      optional: false,
      currentValue: {
        maxNumNeighbors: {
          title: 'Max Number of Neighbors',
          tooltip: 'Tight, consistent lines: 20–40. Cross-lines/obliques/complex missions: 50–80.',
          optional: false,
          currentValue: 20,
        },
        maxDistance: {
          title: 'Max Distance',
          tooltip:
            'Set to your flight spacing: Typical grid with 70–80% overlap: 60–150 (meters). Wide baselines / cross-grid obliques: 150–300.',
          optional: false,
          currentValue: 150,
        },
      },
    },
    sequentialMatcher: {
      title: 'Sequential Matcher',
      optional: false,
      currentValue: {
        overlap: {
          title: 'Overlap',
          tooltip: (
            <>
              How many neighbors each image matches to along the sequence (i±k).
              <br />
              <br />
              Smooth, high overlap flight lines: 8–12.
              <br />
              Faster speed / motion blur / sparser overlap: 12–20.
              <br />
              Mixed oblique + cross-lines: 16–24.
            </>
          ),
          optional: false,
          currentValue: 6,
        },
        quadraticOverlap: {
          title: 'Quadratic Overlap',
          tooltip: (
            <>
              Controls whether to use quadratic overlap calculation for sequential matching.
              <br />
              <br />
              When enabled (true), uses a more sophisticated overlap calculation that better handles
              varying image spacing and flight patterns. Keep enabled for most cases.
              <br />
              <br />
              Disable (false) only if you have uniform, consistent spacing and want simpler linear
              overlap.
            </>
          ),
          optional: false,
          currentValue: true,
        },
      },
    },
    exhaustiveMatcher: {
      title: 'Exhaustive Matcher',
      optional: false,
      currentValue: {
        runExhaustiveMatcher: {
          title: 'Run Exhaustive Matcher',
          tooltip: (
            <>
              Typically slow because it matches every image to every other image.
              <br />
              <br />
              If exhaustive runner is enabled, spatial and sequential matching will not run.
            </>
          ),
          optional: false,
          currentValue: false,
        },
      },
    },
    siftMatching: {
      title: 'SIFT Matching',
      optional: false,
      currentValue: {
        maxRatio: {
          title: 'Max Ratio',
          tooltip: (
            <>
              (Lowe&apos;s ratio, default 0.8) — Main precision/recall dial.
              <br />
              <br />
              Repetitive textures (roofs/fields): 0.70–0.75 (stricter, fewer false matches).
              <br />
              Low-texture (snow/crops/water): 0.82–0.85 (looser, more matches).
            </>
          ),
          optional: false,
          currentValue: 0.8,
        },
        maxDistance: {
          title: 'Max Distance',
          tooltip: (
            <>
              (descriptor distance, default 0.7) — Secondary filter.
              <br />
              <br />
              Rarely needed; tighten to 0.6–0.65 if you&apos;re still getting outliers, or loosen
              slightly (0.75–0.8) in very sparse scenes.
            </>
          ),
          optional: false,
          currentValue: 0.7,
        },
        guidedMatching: {
          title: 'Guided Matching',
          tooltip: 'Keep on especially when initial matches/inliers are low (obliques/urban).',
          optional: false,
          currentValue: true,
        },
        maxNumMatches: {
          title: 'Max Number of Matches',
          tooltip:
            'If each pair is extremely textured, cap at 10k–20k to save memory; if pairs are sparse, leave default.',
          optional: false,
          currentValue: 32768,
        },
      },
    },
    twoViewGeometry: {
      title: 'Two View Geometry',
      optional: false,
      currentValue: {
        minNumInliers: {
          title: 'Min Number of Inliers',
          tooltip: (
            <>
              Controls when a pair &apos;counts.&apos;
              <br />
              <br />
              Dense, easy overlaps: 25–40 (more robust graph).
              <br />
              Sparse/low-texture: 10–15.
            </>
          ),
          optional: false,
          currentValue: 15,
        },
        maxError: {
          title: 'Max Error',
          tooltip: (
            <>
              Tighten for clean data; loosen for noisy.
              <br />
              <br />
              Good calibration & moderate GSD: 2–3 px.
              <br />
              Heavy distortion/noisy EXIF: 4–6 px.
            </>
          ),
          optional: false,
          currentValue: 3,
        },
        confidence: {
          title: 'Confidence',
          tooltip: (
            <>
              For small to medium drone reconstructions (&lt;200 images): keep 0.999 — it ensures
              solid pair verification.
              <br />
              <br />
              For large, uniform flights (&gt;1000 images): drop to 0.995 or 0.99 to cut runtime
              without noticeably hurting accuracy.
              <br />
              <br />
              Avoid going lower than 0.98 — you start risking missing valid matches when overlap is
              weak or noise is high.
            </>
          ),
          optional: false,
          currentValue: 0.999,
        },
        maxNumTrials: {
          title: 'Max Number of Trials',
          tooltip: 'If pairs struggle to verify, bump to 20000–50000.',
          optional: false,
          currentValue: 10000,
        },
        minInlierRatio: {
          title: 'Min Inlier Ratio',
          tooltip: 'If early aborts happen on tough pairs, drop to 0.1–0.2.',
          optional: false,
          currentValue: 0.25,
        },
      },
    },
    mapper: {
      title: 'Mapper',
      optional: false,
      currentValue: {
        useGpsData: {
          title: 'Use GPS Data',
          tooltip: (
            <>
              If true it will consider the positions of the images including the standard deviation
              values.
              <br />
              <br />
              This is useful for standard drone flights.
              <br />
              <br />
              Consider turning off if GPS data is either not available or very inaccurate.
              <br />
              <br />
              GPS and RTK data will then be ignored when aligning the images.
            </>
          ),
          optional: false,
          hidden: true,
          currentValue: true,
        },
        useRobustLossOnPriorPosition: {
          title: 'Use Robust Loss on Prior Position',
          tooltip: (
            <>
              Set to true when some priors are bad/outliers (multipath, jumps, mixed RTK + non-RTK).
              <br />
              <br />
              A robust loss down-weights those without throwing the whole BA off.
              <br />
              <br />
              False if your priors are uniformly high-quality (clean RTK).
            </>
          ),
          optional: false,
          currentValue: false,
        },
        stdX: {
          title: 'Standard Deviation X',
          tooltip: 'Set this to use specific standard deviation for the image GPS position',
          optional: true,
          excluded: true,
          currentValue: 0.03,
        },
        stdY: {
          title: 'Standard Deviation Y',
          tooltip: 'Set this to use specific standard deviation for the image GPS position',
          optional: true,
          excluded: true,
          currentValue: 0.03,
        },
        stdZ: {
          title: 'Standard Deviation Z',
          tooltip: 'Set this to use specific standard deviation for the image GPS position',
          optional: true,
          excluded: true,
          currentValue: 0.03,
        },
        initMinNumInliers: {
          title: 'Init Min Number of Inliers',
          tooltip: (
            <>
              Important. If the first pair struggles to verify (low texture/oblique), drop to 60–90.
              <br />
              <br />
              For easy, dense overlap, raise to 120–180 for robustness.
            </>
          ),
          optional: false,
          currentValue: 100,
        },
        initMinTriAngle: {
          title: 'Init Min Triangulation Angle',
          tooltip: (
            <>
              Important. Controls baseline of the seed.
              <br />
              <br />
              For mostly nadir flights (small parallax), consider 12–15; for obliques keep 16–20 to
              get a stiff seed.
            </>
          ),
          optional: false,
          currentValue: 16.0,
        },
        initMaxForwardMotion: {
          title: 'Init Max Forward Motion',
          tooltip:
            'Sometimes helpful. If the seed keeps failing due to near-pure forward motion, relax slightly to 0.97–0.98.',
          optional: false,
          currentValue: 0.95,
        },
        initMaxError: {
          title: 'Init Max Error',
          tooltip: (
            <>
              Important. Tighter for good optics/calibration: 2–3.
              <br />
              <br />
              Looser for noisy distortion/GPS: 4–6.
            </>
          ),
          optional: false,
          currentValue: 3,
        },
        triMinAngle: {
          title: 'Triangulation Min Angle',
          tooltip: (
            <>
              Important (lots of nadir imagery benefits here).
              <br />
              <br />
              Raise to 2–3° for nadir grids to avoid skinny (unstable) triangles.
            </>
          ),
          optional: false,
          currentValue: 1.5,
        },
        triMergeMaxReprojError: {
          title: 'Triangulation Merge Max Reprojection Error',
          tooltip:
            'Occasionally (nadir imagery benefits here). If recon drifts, reduce reproj errors to 3 px; if point growth stalls, keep defaults.',
          optional: false,
          currentValue: 4,
        },
        triCompleteMaxReprojError: {
          title: 'Triangulation Complete Max Reprojection Error',
          tooltip:
            'Occasionally (nadir imagery benefits here). If recon drifts, reduce reproj errors to 3 px; if point growth stalls, keep defaults.',
          optional: false,
          currentValue: 4.0,
        },
        triCreateMaxAngleError: {
          title: 'Triangulation Create Max Angle Error',
          tooltip:
            'Occasionally (nadir imagery benefits here). If recon drifts, increase to 3-5°; if point growth stalls, keep defaults.',
          optional: false,
          currentValue: 2.0,
        },
        triContinueMaxAngleError: {
          title: 'Triangulation Continue Max Angle Error',
          tooltip:
            'Occasionally. If recon drifts, increase to 3-5°; if point growth stalls, keep defaults.',
          optional: false,
          currentValue: 2,
        },
        absPoseMaxError: {
          title: 'Absolute Pose Max Error',
          tooltip:
            "Useful. Tighter if intrinsics are good (8–10); looser if distortion isn't well modeled (12–15).",
          optional: false,
          currentValue: 12.0,
        },
        absPoseMinNumInliers: {
          title: 'Absolute Pose Min Number of Inliers',
          tooltip: (
            <>
              Important.
              <br />
              <br />
              Low-texture scenes: 20–25; textured/urban: 30–50.
            </>
          ),
          optional: false,
          currentValue: 30,
        },
        filterMaxReprojError: {
          title: 'Filter Max Reprojection Error',
          tooltip: 'Important for cleanliness. If you see drift/noisy points, try 3 px',
          optional: false,
          currentValue: 4.0,
        },
        filterMinTriAngle: {
          title: 'Filter Min Triangulation Angle',
          tooltip: (
            <>
              Important for cleanliness.
              <br />
              <br />
              If you see drift/noisy points, try 3 px and 2–3° respectively.
            </>
          ),
          optional: false,
          currentValue: 1.5,
        },
        minFocalLengthRatio: {
          title: 'Min Focal Length Ratio',
          tooltip: (
            <>
              Sometimes helpful.
              <br />
              <br />
              If EXIF is messy or zoom varies, tighten (e.g., 0.5–2.0) to prevent crazy focal
              estimates.
            </>
          ),
          optional: false,
          currentValue: 0.5,
        },
        maxFocalLengthRatio: {
          title: 'Max Focal Length Ratio',
          tooltip: (
            <>
              Sometimes helpful.
              <br />
              <br />
              If EXIF is messy or zoom varies, tighten (e.g., 0.5–2.0) to prevent crazy focal
              estimates.
            </>
          ),
          optional: false,
          currentValue: 2,
        },
        baRefineFocalLength: {
          title: 'BA Refine Focal Length',
          tooltip: (
            <>
              Refine focal length in bundle adjustment.
              <br />
              <br />
              ENABLE (true): If EXIF focal is approximate but close; helps fine-tune and reduce
              reprojection error.
              <br />
              <br />
              DISABLE (false): If you see height flattening/bowing (doming) in nadir; fix scale
              first (tight ratios or GCP/GNSS), then optionally re-enable for a final polish.
              <br />
              <br />
              Default: true for general scenes; false or tightly bounded for pure nadir without
              GCPs/obliques.
            </>
          ),
          optional: false,
          currentValue: true,
        },
        baRefinePrincipalPoint: {
          title: 'BA Refine Principal Point',
          tooltip: (
            <>
              Refine principal point (cx, cy) in bundle adjustment.
              <br />
              <br />
              ENABLE (true): For oblique/mixed imagery or if you know the principal point may be
              off.
              <br />
              <br />
              DISABLE (false): For pure nadir with weak constraints—moving cx,cy can worsen
              doming/bending.
              <br />
              <br />
              Default: false for nadir corridor sets; true only if you have strong geometry
              (obliques / cross-strips / GCPs).
            </>
          ),
          optional: false,
          currentValue: false,
        },
        baRefineExtraParams: {
          title: 'BA Refine Extra Params',
          tooltip: (
            <>
              Refine extra distortion params (e.g., radial/tangential) in bundle adjustment.
              <br />
              <br />
              ENABLE (true): If using a richer model (OPENCV/RADIAL) and you have strong tie
              geometry; helps de-warp corners and improve edges.
              <br />
              <br />
              DISABLE (false): Early on in nadir-only projects—extra freedom can trade distortion
              for terrain curvature (short buildings / bowl). Re-enable at the end once the model is
              stable.
              <br />
              <br />
              Default: false for initial nadir runs; true for final BA after alignment is good.
            </>
          ),
          optional: false,
          currentValue: true,
        },
      },
    },
    modelAlignment: {
      title: 'Model Alignment (GPS)',
      optional: true,
      excluded: true,
      currentValue: {
        alignmentMaxError: {
          title: 'Alignment Max Error',
          tooltip:
            "Automatically adjusted based on the STD deviation value in the image EXIF data. Try setting it manually if the model can't be aligned.",
          optional: true,
          excluded: true,
          currentValue: 0,
        },
      },
    },
    imageUndistortion: {
      title: 'Image Undistortion',
      optional: false,
      currentValue: {
        maxImageSize: {
          title: 'Max Image Size',
          tooltip:
            'Biggest contributor to runtime and quality, will affect patch match stereo time consumption.',
          optional: false,
          currentValue: 1300,
        },
      },
    },
    patchMatchStereo: {
      title: 'Patch Match Stereo',
      optional: false,
      currentValue: {
        numSamples: {
          title: 'Number of Samples',
          tooltip:
            'More samples = better minima search, slower. Balanced: 15–20 (default 15). Tough/low-texture: 20–30.',
          optional: false,
          currentValue: 15,
        },
        numIterations: {
          title: 'Number of Iterations',
          tooltip: 'Balanced: 5–7. Hard scenes: 7–9.',
          optional: false,
          currentValue: 5,
        },
        windowRadius: {
          title: 'Window Radius',
          tooltip: (
            <>
              Controls NCC window.
              <br />
              <br />
              Nadir, decent texture: 4–6 (default 5 is fine).
              <br />
              Very low-texture (snow/crops): 6–7 (smoother but blurrier edges).
            </>
          ),
          optional: false,
          currentValue: 3,
        },
        sigmaColor: {
          title: 'Sigma Color',
          tooltip: 'Raise to 0.3–0.4 for harsh lighting.',
          optional: false,
          currentValue: 0.2,
        },
        sigmaSpatial: {
          title: 'Sigma Spatial',
          tooltip: 'Set 3–6 only if you see speckle.',
          optional: false,
          excluded: true,
          currentValue: -1,
        },
        geomConsistency: {
          title: 'Geometric Consistency',
          tooltip: (
            <>
              If true it will run a second pass of the patch match stereo to do geometric checks for
              every view.
              <br />
              <br />
              This increases the runtime significantly but can improve the quality of the point
              cloud.
            </>
          ),
          optional: false,
          currentValue: true,
        },
        geomConsistencyRegularizer: {
          title: 'Geometric Consistency Regularizer',
          tooltip: 'Balanced: 0.3–0.5. Very noisy matches: 0.5–0.7 (stronger smoothing).',
          optional: false,
          currentValue: 0.3,
        },
        geomConsistencyMaxCost: {
          title: 'Geometric Consistency Max Cost',
          tooltip: 'Lower to 2–2.5 to be stricter (may lose thin structures).',
          optional: false,
          currentValue: 2,
        },
        filterMinNumConsistent: {
          title: 'Filter Min Number of Consistent',
          tooltip: 'Cleaner: 3 views. Set to 2 if overlap is low.',
          optional: false,
          currentValue: 3,
        },
        filterGeomConsistencyMaxCost: {
          title: 'Filter Geometric Consistency Max Cost',
          tooltip: "Raise to 1.5–2 if you're over-filtering valid depths.",
          optional: false,
          currentValue: 0.8,
        },
        filterMinNcc: {
          title: 'Filter Min NCC',
          tooltip: (
            <>
              Clean/textured scenes: 0.2–0.3.
              <br />
              <br />
              Low-texture: 0.1–0.2 (accepts noisier depths).
            </>
          ),
          optional: false,
          currentValue: 0.2,
        },
        minTriangulationAngle: {
          title: 'Min Triangulation Angle',
          tooltip: (
            <>
              Use 2–3° to avoid grazing-parallax noise.
              <br />
              <br />
              (obliques can use 1–2° if coverage is great).
            </>
          ),
          optional: false,
          currentValue: 2,
        },
        filterMinTriangulationAngle: {
          title: 'Filter Min Triangulation Angle',
          tooltip: (
            <>
              For low-parallax sets, 2–2.5° can keep more points.
              <br />
              <br />
              For cleaner meshes, 3–4°.
            </>
          ),
          optional: false,
          currentValue: 5,
        },
      },
    },
    stereoFusion: {
      title: 'Stereo Fusion',
      optional: false,
      currentValue: {
        inputType: {
          title: 'Input Type',
          tooltip:
            'Set to geometric only if you had geometric consistency enabled in patch match stereo.',
          optional: false,
          currentValue: 'geometric',
          values: ['photometric', 'geometric'],
        },
        maxReprojError: {
          title: 'Max Reprojection Error',
          tooltip: (
            <>
              Controls how tightly fused points must agree between images (in pixels).
              <br />
              <br />
              For well-calibrated drones, 2 px (default) is good.
              <br />
              <br />
              If you&apos;re losing points (too strict), loosen slightly: 3–4 px.
              <br />
              <br />
              If you see noisy or &apos;ghost&apos; layers, tighten to 1.5–2 px.
            </>
          ),
          optional: false,
          currentValue: 2.0,
        },
        maxDepthError: {
          title: 'Max Depth Error',
          tooltip: (
            <>
              Relative depth difference threshold (fraction of distance).
              <br />
              <br />
              Default 0.01 (≈1%) is good for most drones.
              <br />
              <br />
              For RTK-accurate geometry and low noise, you can try 0.005 (stricter).
              <br />
              <br />
              For low-texture, noisy scenes, 0.015–0.02 helps retain more points.
            </>
          ),
          optional: false,
          currentValue: 0.01,
        },
        maxNormalError: {
          title: 'Max Normal Error',
          tooltip: (
            <>
              Maximum angle (degrees) between normals for fusing points.
              <br />
              <br />
              Drone scenes are mostly planar (ground/buildings), so keep 10–15°.
              <br />
              <br />
              For rough terrain or obliques, go up to 20°.
            </>
          ),
          optional: false,
          currentValue: 10.0,
        },
        minNumPixels: {
          title: 'Min Number of Pixels',
          tooltip: (
            <>
              Affects the minimum and maximum patch size when checking support.
              <br />
              <br />
              Lowering min_num_pixels (to 3–4) can recover small features.
              <br />
              <br />
              Raising it filters small clusters.
            </>
          ),
          optional: false,
          currentValue: 5,
        },
        maxNumPixels: {
          title: 'Max Number of Pixels',
          tooltip: 'Leave max_num_pixels as-is unless you see very dense clumps or RAM issues.',
          optional: false,
          currentValue: 10000,
        },
      },
    },
    pointCloud: {
      title: 'Point Cloud',
      optional: false,
      currentValue: {
        submitSkyViewPipelineJob: {
          title: 'Submit SkyView Pipeline Job',
          optional: false,
          currentValue: true,
        },
      },
    },
    pointCloudAlignment: {
      title: 'Point Cloud Alignment',
      optional: false,
      currentValue: {
        useThreePointsFit: {
          title: 'Use 3-Points Fit',
          optional: false,
          currentValue: false,
        },
        skipGcpAlignment: {
          title: 'Skip GCP Alignment',
          optional: false,
          currentValue: false,
        },
        sourceMesh: {
          title: 'Source Mesh',
          optional: false,
          currentValue: 'delaunay',
          values: ['delaunay', 'poisson'],
        },
        translationObjective: {
          title: 'Translation Objective',
          optional: false,
          currentValue: 'L2',
          values: ['L2', 'L1', 'LinfYawOnly'],
        },
        lockScale: {
          title: 'Lock Scale',
          optional: false,
          currentValue: false,
        },
        axisYawOnly: {
          title: 'Axis Yaw Only',
          optional: false,
          currentValue: false,
        },
        lockRotation: {
          title: 'Lock Rotation',
          optional: false,
          currentValue: false,
        },
      },
    },
  } satisfies JsonTemplate<GenerateDataColmapType>);

  const odmCustomConfiguration = useRef<JsonTemplate<OdmCustomType>>({
    type: {
      title: 'Type',
      optional: false,
      hidden: true,
      currentValue: 'odm_custom',
      values: ['odm_custom'],
    },
    settings: {
      title: 'Type',
      optional: false,
      currentValue: {
        pipeline: {
          title: 'Pipeline',
          optional: false,
          currentValue: 'align_and_generate',
          values: ['align_and_generate', 'align_only'],
        },
        featureQuality: {
          title: 'Aligning: Kvalitet',
          optional: false,
          currentValue: 'ultra',
          values: ['ultra', 'high', 'medium', 'low'],
        },
        featureType: {
          title: 'Aligning: FeatureType',
          optional: false,
          currentValue: 'sift',
          values: ['sift', 'dspsift', 'akaze', 'hahog', 'orb'],
        },
        minFeatures: {
          title: 'Aligning: Minimum features',
          optional: false,
          currentValue: 9000,
        },
        numberOfNeighbours: {
          title: 'Aligning: Number of neighbors',
          optional: false,
          currentValue: 10,
        },
        gpsAccuracy: {
          title: 'Aligning: GPS-noggrannhet',
          optional: true,
          currentValue: 3,
        },
        forceGps: {
          title: 'Använd GPS även när GCP-punkter finns.',
          optional: false,
          currentValue: false,
        },
        pcQuality: {
          title: 'Punktmoln: Kvalitet',
          optional: false,
          currentValue: 'high',
          values: ['ultra', 'high', 'medium', 'low'],
        },
        pcFilter: {
          title: 'Punktmoln: Median outlier filter',
          optional: false,
          currentValue: 1.25,
        },
        sampleRadius: {
          title: 'Punktmoln: Sample radius',
          optional: false,
          currentValue: 0.02,
        },
        morphologicalFilterScalar: {
          title: 'Klassifiering: Smrf scalar',
          optional: false,
          currentValue: 1.25,
        },
        morphologicalFilterSlope: {
          title: 'Klassifiering: Smrf slope',
          optional: false,
          currentValue: 0.5,
        },
        morphologicalThreshold: {
          title: 'Klassifiering: Smrf threshold',
          optional: false,
          currentValue: 0.2,
        },
        morphologicalFilterWindow: {
          title: 'Klassifiering: Smrf window',
          optional: false,
          currentValue: 18,
        },
        orthopotoResolution: {
          title: 'Ortofoto: Upplösning (GSD)',
          optional: false,
          currentValue: 0.02,
        },
        use3dMesh: {
          title: 'Ortofoto: Använd 3D-mesh för ortofoto',
          optional: false,
          currentValue: false,
        },
        skip3dModel: {
          title: '3D-modell: Skippa 3D-modell',
          optional: false,
          currentValue: false,
        },
        meshSize: {
          title: '3D-modell: Antal trianglar',
          optional: false,
          currentValue: 150000,
        },
      },
    },
  } satisfies JsonTemplate<OdmCustomType>);

  const classicCustomConfiguration = useRef<JsonTemplate<GenerateDataClassicCustomType>>({
    type: {
      title: 'Type',
      optional: false,
      currentValue: 'generate_data_classic_custom',
      values: ['generate_data_classic_custom'],
    },
    expandBoundingBoxMeters: {
      title: 'expandBoundingBoxMeters',
      optional: true,
      excluded: true,
      currentValue: 0,
    },
    sourceData: {
      title: 'Source data',
      optional: true,
      currentValue: getSourceDataDefaultTemplate(),
    },
    alignment: {
      title: 'Alignment',
      optional: true,
      currentValue: getAlignmentDefaultTemplate(),
    },
    depthMaps: {
      title: 'Depth maps',
      optional: true,
      currentValue: {
        resetDepthMaps: {
          title: 'Reset depth maps',
          optional: false,
          currentValue: false,
        },
        quality: {
          title: 'Quality',
          optional: false,
          currentValue: 'medium',
          values: ['ultra', 'high', 'medium', 'low', 'lowest'],
        },
        filtering: {
          title: 'Filtering',
          optional: false,
          currentValue: 'aggressive',
          values: ['off', 'mild', 'moderate', 'aggressive'],
        },
      },
    },
    pointCloud: {
      title: 'Point cloud',
      optional: true,
      currentValue: {
        resetPointCloud: {
          title: 'Force reset point cloud',
          currentValue: false,
          optional: false,
        },
        classifyPoints: {
          title: 'Classify points',
          currentValue: true,
          optional: false,
        },
        uniformSampling: {
          title: 'Uniform sampling',
          currentValue: false,
          optional: false,
        },
        pointsSpacing: {
          title: 'Point spacing (Uniform sampling)',
          currentValue: 0,
          optional: false,
        },
        minConfidence: {
          title: 'Min confidence',
          currentValue: 0,
          optional: false,
        },
        smoothingRadius: {
          title: 'Smoothing radius',
          currentValue: 0,
          optional: false,
        },
        smrfMaxAngle: {
          title: 'SMRF: Max angle',
          currentValue: 45,
          optional: false,
        },
        smrfMaxTerrainSlope: {
          title: 'SMRF: Max terrain slope',
          currentValue: 80,
          optional: false,
        },
        smrfErosionRadius: {
          title: 'SMRF: Erosion radius',
          currentValue: 0.1,
          optional: false,
        },
        smrfMaxDistance: {
          title: 'SMRF: Max distance',
          currentValue: 0.06,
          optional: false,
        },
        smrfCellSize: {
          title: 'SMRF: Cell size',
          currentValue: 20,
          optional: false,
        },
        submitSkyViewPipelineJob: {
          title: 'Submit SkyView Pipeline Job',
          currentValue: true,
          optional: false,
        },
      },
    },
    tiledModel: {
      title: 'Tiled model',
      optional: true,
      currentValue: {
        faceCount: {
          title: 'faceCount',
          currentValue: 4000,
          optional: false,
        },
        runInParallel: {
          title: 'Run in parallel',
          currentValue: true,
          optional: false,
        },
        submitSkyViewPipelineJob: {
          title: 'submitSkyViewPipelineJob',
          currentValue: true,
          optional: false,
        },
      },
    },
    dem: getDefaultDemSettings({ hideCustomSettings: false, excludedByDefault: false }),
    orthoMosaic: {
      title: 'Ortho mosaic',
      optional: true,
      currentValue: {
        surfaceData: {
          title: 'surfaceData',
          currentValue: 'dem',
          optional: false,
          values: ['model', 'pointCloud', 'dem'],
        },
        gsd: {
          title: 'gsd',
          currentValue: 0.02,
          optional: false,
        },
        maxLodLevel: {
          title: 'maxLodLevel (20-23)',
          currentValue: 23,
          excluded: true,
          optional: true,
        },
        exportedTifGsd: {
          title: 'gsd (exported TIF)',
          currentValue: 0.02,
          optional: false,
        },
        submitSkyViewPipelineJob: {
          title: 'submitSkyViewPipelineJob',
          currentValue: true,
          optional: false,
        },
      },
    },
    model: {
      title: 'Model (Used for orthomosaic if surface data is "model")',
      optional: true,
      excluded: true,
      currentValue: {
        sourceData: {
          title: 'Source data',
          currentValue: 'pointCloud',
          optional: false,
          values: ['pointCloud', 'depthMaps'],
        },
        interpolation: {
          title: 'interpolation',
          currentValue: 'enabled',
          optional: false,
          values: ['disabled', 'enabled', 'extrapolated'],
        },
        surfaceType: {
          title: 'Surface type',
          currentValue: 'heightField',
          optional: false,
          values: ['heightField', 'arbitrary'],
        },
        faceCountMode: {
          title: 'Face count mode',
          currentValue: 'high',
          optional: false,
          values: ['low', 'medium', 'high'],
        },
        customFaceCount: {
          title: 'Custom face count (overrides face count mode)',
          currentValue: 150000,
          optional: true,
          excluded: true,
        },
      },
    },
    terrainModel: {
      title: 'Terrain model (ADDON)',
      optional: true,
      excluded: true,
      currentValue: {
        sourceData: {
          title: 'Source data',
          currentValue: 'pointCloud',
          optional: false,
          values: ['pointCloud', 'dem'],
        },
        interpolation: {
          title: 'Interpolation',
          currentValue: 'disabled',
          optional: false,
          values: ['disabled', 'enabled', 'extrapolated'],
        },
        surfaceType: {
          title: 'Surface type',
          currentValue: 'arbitrary',
          optional: false,
          values: ['heightField', 'arbitrary'],
        },
        faceCountMode: {
          title: 'Face count mode',
          currentValue: 'high',
          optional: false,
          values: ['low', 'medium', 'high'],
        },
        customFaceCount: {
          title: 'Custom face count (overrides face count mode)',
          currentValue: 150000,
          optional: true,
        },
      },
    },
    filteredPointCloud: {
      title: 'Filtered point cloud (ADDON)',
      optional: true,
      excluded: true,
      currentValue: {
        spacing: {
          title: 'Spacing (m)',
          currentValue: 0.02,
          optional: false,
        },
        classes: {
          title: 'Point classes',
          currentValue: 'GROUND',
          optional: false,
          values: ['ALL', 'GROUND'],
        },
        format: {
          title: 'File format',
          currentValue: 'LAS',
          optional: false,
          values: ['LAS', 'LAZ', 'DXF'],
        },
      },
    },
    obj: {
      title: 'OBJ (ADDON)',
      optional: true,
      excluded: true,
      currentValue: {
        triangleCount: {
          title: 'Custom triangle count',
          currentValue: 50000,
          optional: true,
          excluded: true,
        },
      },
    },
  } satisfies JsonTemplate<GenerateDataClassicCustomType>);

  const pmCustomConfiguration = useRef<JsonTemplate<GenerateDataPmCustomType>>({
    type: {
      title: 'Type',
      optional: false,
      currentValue: 'generate_data_pm_custom',
      values: ['generate_data_pm_custom'],
    },
    settings: {
      currentValue: {
        keepAlive: {
          currentValue: false,
          optional: true,
          title: 'Keep alive',
        },
        type: {
          title: 'Pipeline type',
          optional: true,
          excluded: true,
          currentValue: 'generate_data',
          values: ['at_only', 'at_and_preview', 'generate_data'],
        },
        resetProject: {
          title: 'Reset project',
          optional: true,
          excluded: true,
          currentValue: false,
        },
        exportPointcloud: {
          title: 'Export point cloud',
          optional: true,
          excluded: true,
          currentValue: false,
        },
        verticalAccuracy: {
          title: 'AT vertical accuracy',
          optional: true,
          excluded: true,
          currentValue: 0.1,
          values: [0, 0.0001, 0.1, 1, 10, 100, 1000000, null],
        },
        horizontalAccuracy: {
          title: 'AT horizontal accuracy',
          optional: true,
          excluded: true,
          currentValue: 0.1,
          values: [0, 0.0001, 0.1, 1, 10, 100, 1000000, null],
        },
        focalLengthAccuracy: {
          title: 'Focal length accuracy',
          optional: true,
          excluded: true,
          currentValue: 0,
          values: [-1, 0, 1, 5, null],
        },
        principalPointAccuracy: {
          title: 'Principal point accuracy',
          optional: true,
          excluded: true,
          currentValue: 0,
          values: [-1, 0, 1, 5, null],
        },
        ratio: {
          title: 'Affinity (false = Calculate, true = Constant)',
          optional: true,
          excluded: true,
          currentValue: false,
          values: [false, true, null],
        },
        skew: {
          title: 'Skew (false = Calculate, true = Constant)',
          optional: true,
          excluded: true,
          currentValue: false,
          values: [false, true, null],
        },
        radialAccuracy: {
          title: 'Radial distortion (false = Standard, true = Use Input)',
          optional: true,
          excluded: true,
          currentValue: false,
          values: [false, true, null],
        },
        tangentialAccuracy: {
          title: 'Tangential distortion (false = Standard, true = Use Input)',
          optional: true,
          excluded: true,
          currentValue: false,
          values: [false, true, null],
        },
        photoSamplingInterval: {
          title: 'Photo sampling interval (null = Best)',
          optional: true,
          excluded: true,
          currentValue: null,
          values: [null, 4, 8, 16],
        },
        meshModelResolution: {
          title: 'Mesh model resolution (null = Best)',
          optional: true,
          excluded: true,
          currentValue: null,
          values: [null, 5, 7.5],
        },
        textureResolution: {
          title: 'Texture resolution (null = Best)',
          optional: true,
          excluded: true,
          currentValue: null,
          values: [null, 0.125, 0.25],
        },
      },
      optional: false,
      title: 'Settings',
    },
  } satisfies JsonTemplate<GenerateDataPmCustomType>);

  if (state === 'pm') {
    return pmCustomConfiguration;
  }

  if (state === 'odm') {
    return odmCustomConfiguration;
  }

  if (state === 'colmap') {
    return colmapConfiguration;
  }

  if (state === 'classic_generate_data') {
    return classicCustomConfiguration;
  }

  if (isOdm) {
    return odmConfiguration;
  }

  if (isPm) {
    return pmConfiguration;
  }

  return classicConfiguration;
};
